/**
 * The guides' words, per platform (docs/mockups/install.html, slides iPhone, Android and
 * In-app; Spec panel 8 has the copy). Pure data, so the screens only lay it out.
 */
import type { InAppApp, IosBar, PlatformInfo } from './platform.ts';

/** A browser button drawn inline in a step ("Tap ⋯ at the bottom right"). */
export type Glyph = 'dots' | 'share' | 'kebab' | 'burger';
/** The little picture under a step: the bit of the browser to look for. */
export type Illustration = 'bar_more' | 'bar_share' | 'bar_ipad' | 'share_rows' | 'add_rows' | 'web_app_toggle' | 'add_button';

export interface GuideStep {
	/** The step, with an optional inline glyph between `lead` and `rest`. */
	lead: string;
	glyph?: Glyph;
	rest?: string;
	small?: string;
	ill?: Illustration;
}

/** Where Nib's pointer sits, aimed at the browser's button: off the edge of our page. */
export type Pointer = 'bottom_right' | 'bottom_centre' | 'top_right' | null;

export interface Guide {
	title: string;
	sub: string;
	steps: GuideStep[];
	foot?: { lead: string; glyph?: Glyph; rest?: string };
	pointer: Pointer;
}

export const GUIDE_TITLE = 'Add Sparrow to your home screen';

export function iosGuide(p: Pick<PlatformInfo, 'iosBrowser' | 'iosBar'>): Guide {
	// The red arrow (`Pointer`) is drawn only when the guide knows where the button is. The
	// line counts the numbered steps, not the taps (Safari's compact bar takes four).
	const sub = 'Three steps. Follow the red arrow.';
	if (p.iosBrowser && p.iosBrowser !== 'safari' && p.iosBrowser !== 'in_app') {
		return {
			title: GUIDE_TITLE,
			sub: p.iosBrowser === 'chrome' ? sub : 'Three steps.',
			steps: [
				{ lead: 'Tap Share', glyph: 'share', rest: 'in the address bar', small: p.iosBrowser === 'chrome' ? 'At the top right' : 'Or in the browser’s ⋯ menu' },
				{ lead: 'Tap Add to Home Screen', small: 'Scroll down a little to find it', ill: 'add_rows' },
				{ lead: 'Tap Add', ill: 'add_button' },
			],
			pointer: p.iosBrowser === 'chrome' ? 'top_right' : null,
		};
	}
	return { ...safariGuide(p.iosBar ?? 'compact_more'), title: GUIDE_TITLE, sub };
}

function safariGuide(bar: IosBar): Omit<Guide, 'title' | 'sub'> {
	if (bar === 'toolbar_share') {
		return {
			steps: [
				{ lead: 'Tap Share', glyph: 'share', rest: 'in the middle of the bottom bar', ill: 'bar_share' },
				{ lead: 'Scroll down, then tap Add to Home Screen', ill: 'add_rows' },
				{ lead: 'Tap Add at the top right', ill: 'add_button' },
			],
			foot: { lead: 'Don’t see Share? Tap the address bar, then look again.' },
			pointer: 'bottom_centre',
		};
	}
	if (bar === 'ipad_top') {
		return {
			steps: [
				{ lead: 'Tap Share', glyph: 'share', rest: 'at the top right', ill: 'bar_ipad' },
				{ lead: 'Tap Add to Home Screen', small: 'Scroll down a little to find it', ill: 'add_rows' },
				{ lead: 'Leave Open as Web App on, then tap Add', small: 'Older iPads just say Add', ill: 'web_app_toggle' },
			],
			pointer: 'top_right',
		};
	}
	return {
		steps: [
			{ lead: 'Tap', glyph: 'dots', rest: 'at the bottom right', small: 'Next to the address', ill: 'bar_more' },
			{ lead: 'Tap Share, then Add to Home Screen', small: 'Scroll down a little to find it', ill: 'share_rows' },
			{ lead: 'Leave Open as Web App on, then tap Add', ill: 'web_app_toggle' },
		],
		foot: { lead: 'Don’t see ⋯? Look for Share', glyph: 'share', rest: 'on the bar.' },
		pointer: 'bottom_right',
	};
}

/** Android without Chrome's prompt: the browser's own menu (Samsung Internet's is at the bottom). */
export function androidGuide(p: Pick<PlatformInfo, 'androidBrowser'>): Guide {
	if (p.androidBrowser === 'samsung') {
		return {
			title: GUIDE_TITLE,
			sub: 'Two steps in the browser’s menu.',
			steps: [
				{ lead: 'Tap', glyph: 'burger', rest: 'at the bottom right' },
				{ lead: 'Tap Add page to, then Home screen' },
			],
			pointer: 'bottom_right',
		};
	}
	if (p.androidBrowser === 'firefox') {
		return {
			title: GUIDE_TITLE,
			sub: 'Two steps in Firefox’s menu.',
			steps: [{ lead: 'Tap', glyph: 'kebab', rest: 'beside the address' }, { lead: 'Tap Add app to Home screen' }],
			pointer: null,
		};
	}
	return {
		title: GUIDE_TITLE,
		sub: 'Two steps in Chrome’s menu.',
		steps: [
			{ lead: 'Tap', glyph: 'kebab', rest: 'at the top right', small: 'If you see “Open in Chrome”, tap that first' },
			{ lead: 'Tap Add to home screen, then Install' },
		],
		pointer: 'top_right',
	};
}

/** Safari on a Mac: File → Add to Dock. */
export const MAC_GUIDE: Guide = {
	title: 'Add Sparrow to your Dock',
	sub: 'One step in Safari’s menu bar.',
	steps: [{ lead: 'Choose File, then Add to Dock', small: 'Sparrow opens in its own window, like an app' }],
	pointer: null,
};

/** The way-out card / sheet, inside an app's own browser (Spec panel 8, "In-app"). */
export interface WayOut {
	browser: 'Safari' | 'Chrome';
	title: string;
	/** "You can't add apps from inside Instagram." */
	why: string;
	/** "Or tap ⋯ up there, then Open in external browser." (the manual way). */
	manual: { lead: string; glyph: Glyph; rest: string };
	cta: string;
}

export function wayOut(app: InAppApp, android: boolean): WayOut {
	const browser = android ? 'Chrome' : 'Safari';
	return {
		browser,
		title: `Open in ${browser} to add Sparrow to your home screen.`,
		why: `You can’t add apps from inside ${app.name}.`,
		manual: { lead: 'Tap', glyph: android ? 'kebab' : 'dots', rest: `up there, then ${android ? app.menu.android : app.menu.ios}.` },
		cta: `Open in ${browser}`,
	};
}

/** The one-tap exits: `x-safari-https://…` on iPhone, an `intent://` into Chrome on Android. */
export function exitLink(url: string, android: boolean): string {
	const u = new URL(url);
	if (android) return `intent://${u.host}${u.pathname}${u.search}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(u.toString())};end`;
	return `x-safari-${u.protocol.replace(':', '')}://${u.host}${u.pathname}${u.search}`;
}
